import React from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { Badge } from '@oysterjs/ui/badge';
import { Field, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Spinner } from '@oysterjs/ui/spinner';
import { Switch } from '@oysterjs/ui/switch';
import { Text } from '@oysterjs/ui/text';
import { AnimatePresence, motion } from 'motion/react';
import { Navigate, useLoaderData, useRevalidator } from 'react-router-dom';
import {
  Action,
  AgencyUsageFieldsFragment,
  AgencyUserState,
  ConsumptionMeterCode,
  Resource,
  useSetAgencyMeterOverageLimitMutation,
  useSetAgencyUserOverageLimitMutation
} from '~/api/graphql';
import { usePermissions } from '~/components/permissions';
import { useCanViewSettingsUsageTab } from '~/components/use-settings-usage-tab-enabled';
import { Await } from '@oysterjs/ui/async';
import { formatUsageUnits } from '~/lib/usage';
import { formatUsd, overageUnitsFromDollars } from '~/components/settings/usage/usage-limits-utils';
import { FloatingPendingChangesBar } from '~/components/settings/floating-pending-changes-bar';

type MeterKey = 'platformUnits' | 'kbPages' | 'voiceMinutes';

type OrgMeterDraft = {
  enabled: boolean;
  /** Raw dollar-amount string while the overage cap is on. */
  dollars?: string;
};

type OrgDraft = Record<MeterKey, OrgMeterDraft>;

/**
 * Per-user draft budgets as raw dollar strings.
 * Empty string means unlimited (cleared).
 */
type UserDraft = Record<string, string>;

const METER_CONFIG: {
  key: MeterKey;
  meterCode: ConsumptionMeterCode;
  name: string;
  unitSingular: string;
  limitField:
    | 'platformUnitOverageLimitDollars'
    | 'kbPageOverageLimitDollars'
    | 'voiceMinuteOverageLimitDollars';
}[] = [
  {
    key: 'platformUnits',
    meterCode: ConsumptionMeterCode.PlatformUnit,
    name: 'Platform units',
    unitSingular: 'unit',
    limitField: 'platformUnitOverageLimitDollars'
  },
  {
    key: 'kbPages',
    meterCode: ConsumptionMeterCode.KbPage,
    name: 'Knowledge base pages',
    unitSingular: 'page',
    limitField: 'kbPageOverageLimitDollars'
  },
  {
    key: 'voiceMinutes',
    meterCode: ConsumptionMeterCode.VoiceMinute,
    name: 'Voice minutes',
    unitSingular: 'minute',
    limitField: 'voiceMinuteOverageLimitDollars'
  }
];

const orgDraftFromLimitDollars = (limitDollars: number | null | undefined): OrgMeterDraft => {
  if (limitDollars == null || limitDollars === 0) {
    return {
      enabled: false
    };
  }
  return {
    enabled: true,
    dollars: String(limitDollars)
  };
};

const normalizeLimitDollars = (value: number | null | undefined): number | null => {
  if (value == null || value === 0) {
    return 0;
  }
  return value;
};

const dollarsEqual = (a: number | null | undefined, b: number | null | undefined): boolean => {
  const na = normalizeLimitDollars(a);
  const nb = normalizeLimitDollars(b);
  if (na === nb) {
    return true;
  }
  if (na == null || nb == null) {
    return false;
  }
  return Math.abs(na - nb) < 0.001;
};

const parseOrgDraftDollars = (draft: OrgMeterDraft): number | null => {
  if (!draft.enabled || !draft.dollars) {
    return 0;
  }
  const parsed = Number.parseFloat(draft.dollars.replace(/[$,]/g, ''));
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  return Math.round(parsed * 100) / 100;
};

/** Empty / whitespace → no overage (0); otherwise a non-negative dollar amount. */
const parseUserDraftDollars = (raw: string | undefined): number | null => {
  const trimmed = (raw ?? '').trim();
  if (trimmed === '') {
    return 0;
  }
  const parsed = Number.parseFloat(trimmed.replace(/[$,]/g, ''));
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  return Math.round(parsed * 100) / 100;
};

const OrgMeterLimitRow = ({
  name,
  unitSingular,
  ratePerUnit,
  draft,
  disabled,
  onChange
}: {
  name: string;
  unitSingular: string;
  ratePerUnit: number;
  draft: OrgMeterDraft;
  disabled: boolean;
  onChange: (next: OrgMeterDraft) => void;
}) => {
  const draftDollars = draft.dollars ? Number.parseFloat(draft.dollars.replace(/[$,]/g, '')) : NaN;
  const unitCount =
    Number.isFinite(draftDollars) && draftDollars >= 0
      ? overageUnitsFromDollars(draftDollars, ratePerUnit)
      : 0;
  const unitLabel = `${unitSingular}s`;

  return (
    <li className="flex items-center gap-x-3 py-4 first:pt-0 last:pb-0">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm/6 font-semibold text-neutral-900 dark:text-white">{name}</p>
        <p className="truncate text-xs/5 text-neutral-500 dark:text-neutral-400">
          {formatUsageUnits(unitCount)} {unitLabel} · {formatUsd(ratePerUnit)}/{unitSingular}
        </p>
      </div>
      <Badge color={draft.enabled ? 'sky' : 'neutral'}>
        {draft.enabled ? 'Capped' : 'No overage'}
      </Badge>
      <Switch
        color="sky"
        checked={draft.enabled}
        disabled={disabled}
        aria-label={`Enable overage for ${name}`}
        onChange={(enabled) =>
          onChange({
            enabled,
            dollars: draft.dollars
          })
        }
      />
      <Field className="w-28 shrink-0">
        <Label className="sr-only">Overage budget for {name}</Label>
        <div className="flex items-center gap-1">
          <span className="text-sm text-neutral-400 dark:text-neutral-500" aria-hidden>
            $
          </span>
          <Input
            type="text"
            inputMode="decimal"
            placeholder="—"
            value={draft.enabled ? draft.dollars : ''}
            disabled={disabled || !draft.enabled}
            onChange={(event) => onChange({ ...draft, dollars: event.currentTarget.value })}
          />
        </div>
      </Field>
    </li>
  );
};

const UserBudgetRow = ({
  name,
  email,
  draftDollars,
  overageDollars,
  disabled,
  onChange
}: {
  name: string;
  email: string;
  draftDollars: string;
  overageDollars: number | null | undefined;
  disabled: boolean;
  onChange: (next: string) => void;
}) => (
  <li className="flex items-center gap-x-3 py-4 first:pt-0 last:pb-0">
    <div className="flex size-10 flex-none items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-700 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
      {name
        .split(' ')
        .map((part) => part[0])
        .join('')
        .toUpperCase()}
    </div>
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm/6 font-semibold text-neutral-900 dark:text-white">{name}</p>
      <p className="truncate text-xs/5 text-neutral-500 dark:text-neutral-400">{email}</p>
    </div>
    <Field className="w-auto shrink-0">
      <Label className="sr-only">Budget for {name}</Label>
      <div className="flex items-center gap-1.5 text-sm text-neutral-900 dark:text-white">
        <span className="tabular-nums">{formatUsd(overageDollars ?? 0)}</span>
        <span className="text-neutral-400 dark:text-neutral-500" aria-hidden>
          /
        </span>
        <div className="flex w-28 items-center gap-1">
          <span className="text-neutral-400 dark:text-neutral-500" aria-hidden>
            $
          </span>
          <Input
            type="text"
            inputMode="decimal"
            placeholder="—"
            value={draftDollars}
            disabled={disabled}
            onChange={(event) => onChange(event.currentTarget.value)}
          />
        </div>
      </div>
    </Field>
  </li>
);

export const SettingsLimitsPage = () => {
  const loader = useLoaderData() as
    | {
        data: Promise<{
          agencyUsage: AgencyUsageFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <React.Suspense
      fallback={
        <div className="flex justify-center py-4">
          <Spinner size="size-6" color="neutral" />
        </div>
      }
    >
      <Await resolve={loader?.data}>
        {(data) => data?.agencyUsage && <SettingsLimitsPageInner agency={data.agencyUsage} />}
      </Await>
    </React.Suspense>
  );
};

const SettingsLimitsPageInner = ({ agency }: { agency: AgencyUsageFieldsFragment }) => {
  const revalidator = useRevalidator();
  const [orgDraft, setOrgDraft] = React.useState<OrgDraft | null>(null);
  const [userDraft, setUserDraft] = React.useState<UserDraft | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  const [setMeterLimit] = useSetAgencyMeterOverageLimitMutation();
  const [setUserLimit] = useSetAgencyUserOverageLimitMutation();

  const agencyId = agency.id;
  const subscription = agency.subscription;
  const allowances = subscription?.currentUsageAllowances;
  const users = (agency.users ?? []).filter((user) => user.state === AgencyUserState.Active);

  // Stable snapshots so drafts only reset when saved values change (not every render).
  const serverOrgDraft = React.useMemo((): OrgDraft | null => {
    if (!subscription || !allowances) {
      return null;
    }
    return {
      platformUnits: orgDraftFromLimitDollars(subscription.platformUnitOverageLimitDollars),
      kbPages: orgDraftFromLimitDollars(subscription.kbPageOverageLimitDollars),
      voiceMinutes: orgDraftFromLimitDollars(subscription.voiceMinuteOverageLimitDollars)
    };
  }, [
    subscription?.platformUnitOverageLimitDollars,
    subscription?.kbPageOverageLimitDollars,
    subscription?.voiceMinuteOverageLimitDollars
  ]);

  const serverUserKey = users
    .map((user) => `${user.id}:${user.platformUnitOverageLimitDollars ?? 'null'}`)
    .join('|');

  const serverUserDraft = React.useMemo((): UserDraft => {
    const next: UserDraft = {};
    if (!serverUserKey) {
      return next;
    }
    for (const entry of serverUserKey.split('|')) {
      const separator = entry.indexOf(':');
      if (separator < 0) {
        continue;
      }
      const id = entry.slice(0, separator);
      const raw = entry.slice(separator + 1);
      next[id] = raw === 'null' ? '' : raw;
    }
    return next;
  }, [serverUserKey]);

  React.useEffect(() => {
    if (serverOrgDraft) {
      setOrgDraft(serverOrgDraft);
    }
    setUserDraft(serverUserDraft);
    setSaveError(null);
  }, [serverOrgDraft, serverUserDraft]);

  const orgDirty = React.useMemo(() => {
    if (!orgDraft || !serverOrgDraft) {
      return false;
    }
    return METER_CONFIG.some((meter) => {
      const draft = orgDraft[meter.key];
      const saved = serverOrgDraft[meter.key];
      if (draft.enabled !== saved.enabled) {
        return true;
      }
      // Any edit to the dollars field counts as dirty, even before parse/round-trip.
      return draft.enabled && draft.dollars !== saved.dollars;
    });
  }, [orgDraft, serverOrgDraft]);

  const userDirty = React.useMemo(() => {
    if (!userDraft) {
      return false;
    }
    return users.some((user) => (userDraft[user.id] ?? '') !== (serverUserDraft[user.id] ?? ''));
  }, [userDraft, serverUserDraft, users]);

  const allUsersShareLimit = React.useMemo((): string | null => {
    if (!userDraft || users.length === 0) {
      return '';
    }
    const values = users.map((user) => userDraft[user.id] ?? '');
    const first = values[0];
    return values.every((value) => value === first) ? first : null;
  }, [userDraft, users]);

  const applyLimitToAllUsers = (value: string) => {
    setUserDraft((prev) => {
      if (!prev) {
        return prev;
      }
      const next = { ...prev };
      for (const user of users) {
        next[user.id] = value;
      }
      return next;
    });
  };

  const dirty = orgDirty || userDirty;

  const discard = () => {
    if (serverOrgDraft) {
      setOrgDraft(serverOrgDraft);
    }
    setUserDraft(serverUserDraft);
    setSaveError(null);
  };

  const saveLimits = async () => {
    if (!agencyId || !orgDraft || !userDraft || !allowances) {
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const mutations: Promise<unknown>[] = [];

      if (orgDirty) {
        for (const meter of METER_CONFIG) {
          const saved = subscription?.[meter.limitField];
          const next = parseOrgDraftDollars(orgDraft[meter.key]);
          if (dollarsEqual(saved, next)) {
            continue;
          }
          mutations.push(
            setMeterLimit({
              variables: {
                agencyId,
                meterCode: meter.meterCode,
                limitDollars: next
              }
            })
          );
        }
      }

      if (userDirty) {
        for (const user of users) {
          const next = parseUserDraftDollars(userDraft[user.id]);
          if (dollarsEqual(user.platformUnitOverageLimitDollars, next)) {
            continue;
          }
          mutations.push(
            setUserLimit({
              variables: {
                agencyUserId: user.id,
                limitDollars: next
              }
            })
          );
        }
      }

      await Promise.all(mutations);
      revalidator.revalidate();
      setSaved(true);
      setTimeout(() => setSaved(false), 5000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save limits.');
      setSaved(true);
      setTimeout(() => setSaved(false), 5000);
    } finally {
      setSaving(false);
    }
  };

  if (!subscription || !allowances || !orgDraft || !userDraft) {
    return (
      <div className="rounded-lg bg-white dark:bg-neutral-800 px-6 py-10 text-center shadow">
        <Text className="text-sm text-neutral-500 dark:text-neutral-400">
          Failed to load usage limits.
        </Text>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="@container">
        <div className="grid grid-cols-1 items-start gap-6 @4xl:grid-cols-2">
          <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="m-0 p-0 text-lg font-medium text-neutral-900 dark:text-white">
                Organization overage
              </h3>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                Cap how far each meter can go beyond the included pool.
              </p>
            </div>
            <div className="px-4 py-5 sm:px-6">
              <ul role="list" className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {METER_CONFIG.map((meter) => (
                  <OrgMeterLimitRow
                    key={meter.key}
                    name={meter.name}
                    unitSingular={meter.unitSingular}
                    ratePerUnit={allowances[meter.key].overageRatePerUnit}
                    draft={orgDraft[meter.key]}
                    disabled={saving}
                    onChange={(next) => setOrgDraft({ ...orgDraft, [meter.key]: next })}
                  />
                ))}
              </ul>
            </div>
          </div>

          <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="m-0 p-0 text-lg font-medium text-neutral-900 dark:text-white">
                Per-user limits
              </h3>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                Set platform unit overage budgets for each user.
              </p>
            </div>
            <div className="px-4 py-5 sm:px-6">
              {users.length === 0 ? (
                <Text className="text-sm text-neutral-500 dark:text-neutral-400">
                  No agency users found.
                </Text>
              ) : (
                <>
                  <div className="mb-4 rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 px-4 py-3">
                    <Field>
                      <Label>Set all active users</Label>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        Applies the same platform unit budget to everyone below. Save to persist.
                      </p>
                      <div className="mt-2 flex w-28 items-center gap-1">
                        <span
                          className="text-sm text-neutral-400 dark:text-neutral-500"
                          aria-hidden
                        >
                          $
                        </span>
                        <Input
                          type="text"
                          inputMode="decimal"
                          placeholder={allUsersShareLimit === null ? 'Mixed' : '—'}
                          value={allUsersShareLimit ?? ''}
                          disabled={saving}
                          aria-label="Platform unit budget for all active users"
                          onChange={(event) => applyLimitToAllUsers(event.currentTarget.value)}
                        />
                      </div>
                    </Field>
                  </div>
                  <ul role="list" className="divide-y divide-neutral-200 dark:divide-neutral-700">
                    {users.map((user) => (
                      <UserBudgetRow
                        key={user.id}
                        name={user.name}
                        email={user.email}
                        draftDollars={userDraft[user.id] ?? ''}
                        overageDollars={user.currentOverageSpendStatus?.overageDollars}
                        disabled={saving}
                        onChange={(next) => setUserDraft({ ...userDraft, [user.id]: next })}
                      />
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <FloatingPendingChangesBar
        show={dirty || saving}
        saving={saving}
        saveLabel="Save Changes"
        onDiscard={discard}
        onSave={saveLimits}
      />
      <AnimatePresence>
        {saved && (
          <motion.div
            initial={{ opacity: 0, translateY: 18 }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: 18 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="pointer-events-auto fixed top-8 right-8 z-50 w-full max-w-sm rounded-lg bg-white dark:bg-neutral-800 shadow-lg outline-1 outline-black/5 dark:outline-white/10"
          >
            <div className="p-4">
              <div className="flex items-start">
                <div className="shrink-0">
                  {!saveError && (
                    <CheckCircleIcon aria-hidden="true" className="size-6 text-green-400" />
                  )}
                  {saveError && <XCircleIcon aria-hidden="true" className="size-6 text-red-400" />}
                </div>
                <div className="ml-3 w-0 flex-1 pt-0.5">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {saveError ? 'Error saving limits' : 'Limits saved'}
                  </p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {saveError
                      ? `There was an error saving: ${saveError}`
                      : 'Your changes have been saved.'}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const SettingsLimitsRoute = () => {
  const canViewUsageTab = useCanViewSettingsUsageTab();
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission(Resource.Subscription, Action.Update);

  if (!canViewUsageTab || !canUpdate) {
    return <Navigate to="/app/settings/usage" replace />;
  }
  return <SettingsLimitsPage />;
};
